import {
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  clearCsvOvertimeReportData,
  clearOvertimeReportData,
  clearPtoReportData,
  getCsvOvertimeReport,
  getEmployeeList,
  getOvertimeReport,
} from '../../../store/ReportsDetailsSlice/actions.js';
import {
  selectCsvOvertimeReportData,
  selectEmployeeData,
  selectOvertimeReportData,
} from '../../../store/ReportsDetailsSlice/selectors.js';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter.jsx';
import { saveCsvToFile } from '../../../utils/saveCsvToFile.js';
import { OvertimeTable } from '../../../components/OvertimeTable/OvertimeTable.jsx';
import { usePaginationControls } from '../hooks/usePaginationControl.jsx';
import { GROUPBY } from '../../../constants/Reports';
import { OvertimeGraphics } from './components/Graphics.jsx';
import SelectRebuild from '../../../components/Inputs/SelectRebuild.jsx';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton.jsx';
import { LineChartIcon } from '../../../assets/svg/LineChartIcon.js';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const PAGINATION = 10;
const ROWS_CAP = 200;

const getReqParams = (param) =>
  param.length === 1 && param[0] === 'ALL' ? undefined : param;

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

const ReportOvertimeScreen = () => {
  const dispatch = useDispatch();
  const [exportType, setExportType] = useState('');
  const [isGraphicsOpen, setIsGraphicsOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const groupByParam = params.get('groupby') || GROUPBY[0].value;
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
      pay_codes: [],
      reasons: [],
    },
  });

  const { overtimeReportData, loadingOvertimeReport } = useSelector(
    selectOvertimeReportData
  );
  const { csvOvertimeReport, loadingCsvOvertimeReport } = useSelector(
    selectCsvOvertimeReportData
  );

  const tableItems = useMemo(() => {
    return Object.entries(overtimeReportData?.data || []);
  }, [overtimeReportData]);
  const pages = useMemo(() => {
    const finalizePage = (acc) => {
      if (acc.currentPage.length) acc.builtPages.push(acc.currentPage);
      acc.currentPage = [];
      acc.currentRows = 0;
    };

    const { builtPages, currentPage } = tableItems.reduce(
      (acc, item) => {
        const personRows = Array.isArray(item[1]) ? item[1].length : 0;
        const personTooBig = personRows > ROWS_CAP;
        const wouldExceedRows = acc.currentRows + personRows > ROWS_CAP;
        const wouldExceedPeople = acc.currentPage.length >= PAGINATION;

        if (personTooBig) {
          finalizePage(acc);
          acc.builtPages.push([item]);
          return acc;
        }

        if (wouldExceedRows || wouldExceedPeople) {
          finalizePage(acc);
        }

        acc.currentPage.push(item);
        acc.currentRows += personRows;
        return acc;
      },
      { builtPages: [], currentPage: [], currentRows: 0 }
    );

    if (currentPage.length) builtPages.push(currentPage);
    return builtPages;
  }, [tableItems]);
  const pagesCount = pages.length || 1;
  const { fromDate, toDate, render } = useDateRangeFilter();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);

  const refactoredPayCodes = useMemo(
    () =>
      overtimeReportData?.pay_codes
        ? overtimeReportData.pay_codes.map((item) => ({ id: item, name: item }))
        : [],
    [overtimeReportData]
  );

  const refactoredReasons = useMemo(
    () =>
      overtimeReportData?.reasons
        ? overtimeReportData.reasons.map((item) => ({ id: item, name: item }))
        : [],
    [overtimeReportData]
  );

  const { emp_code: employees, pay_codes: payCodes, reasons } = watch();

  const employeeParam = useMemo(() => createParams(employees), [employees]);
  const paycodesParam = useMemo(() => createParams(payCodes), [payCodes]);
  const reasonsParam = useMemo(() => createParams(reasons), [reasons]);
  const { paginationComponent, pageParam } = usePaginationControls({
    loading: loadingOvertimeReport,
    pagesCount,
  });

  const headerItems = Object.entries(overtimeReportData?.header || []);

  const tableData = useMemo(() => {
    return pages[+pageParam - 1] || [];
  }, [pages, pageParam]);

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupByParam,
      emp_code: getReqParams(employeeParam),
      pay_codes: getReqParams(paycodesParam),
      reasons: getReqParams(reasonsParam),
    };
  }, [
    fromDate,
    toDate,
    groupByParam,
    employeeParam,
    paycodesParam,
    reasonsParam,
  ]);

  const handleChangeGroupBy = (e) => {
    const {
      target: { value },
    } = e;
    setParams((prev) => {
      if (!value) {
        prev.delete('groupby');
      } else {
        if (value === 'Group') {
          prev.delete('employee');
        }
        prev.set('groupby', value);
      }
      return prev;
    });
    dispatch(clearPtoReportData());
  };
  const renderGroupByValue = (selected) => {
    return GROUPBY.find((it) => it.value === selected)?.title || '';
  };
  const handleDownloadReport = (type) => {
    setExportType(type);
    dispatch(getCsvOvertimeReport({ ...param, [type]: 1 }));
  };

  const handleFindClick = () => {
    dispatch(getOvertimeReport(param));
  };

  const toggleShowGraphics = () => setIsGraphicsOpen((p) => !p);

  useEffect(
    () => {
      dispatch(getOvertimeReport(param));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => {
      dispatch(getEmployeeList());
      return () => dispatch(clearOvertimeReportData());
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => {
      setParams((prev) => {
        prev.delete('page');
        return prev;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overtimeReportData]
  );
  useEffect(() => {
    if (csvOvertimeReport?.data) {
      saveCsvToFile(csvOvertimeReport.data, csvOvertimeReport.filename);
      dispatch(clearCsvOvertimeReportData());
      setExportType('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvOvertimeReport]);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <Select
          sx={{
            height: '36px',
          }}
          id="Groupby-selector"
          value={groupByParam}
          onChange={handleChangeGroupBy}
          renderValue={renderGroupByValue}
          placeholder="Group by"
          labelId="groupby-select-label"
          fullWidth
        >
          {GROUPBY.map((it) => (
            <MenuItem key={it.value} value={it.value}>
              <ListItemText primary={it.title} />
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={() => handleDownloadReport('payroll_csv')}
            disabled={loadingCsvOvertimeReport && exportType === 'payroll_csv'}
            text={'Export CSV'}
          />
          <ExportCsvButton
            onClick={() => handleDownloadReport('payroll_all_csv')}
            disabled={
              loadingCsvOvertimeReport && exportType === 'payroll_all_csv'
            }
            text={'Export All CSV'}
          />
        </Grid>
      ),
    },
    {
      key: 'reasons',
      label: 'Reasons',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={refactoredReasons}
          name="reasons"
          label="Reasons"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingOvertimeReport}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'pay_codes',
      label: 'Pay Code',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={refactoredPayCodes}
          name="pay_codes"
          label="Pay Code"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingOvertimeReport}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'graphics',
      label: '',
      show: true,
      renderComponent: () => (
        <Button
          disabled={tableData.length === 0}
          variant="contained"
          onClick={toggleShowGraphics}
          sx={{
            whiteSpace: 'nowrap',
            mt: '26px',
            color: '#fff',
            width: '100%',
            backgroundColor: '#000',
            transition: 'all 0.2s linear',
            '&:hover': {
              backgroundColor: '#000',
              opacity: 0.8,
            },
          }}
          startIcon={<LineChartIcon />}
        >
          Show graphics
        </Button>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Overtime Report Details" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Box
          sx={{ flexDirection: 'column' }}
          className="d-flex justify-content-between"
        >
          <Grid container sx={{ width: '100%', mb: '24px' }}>
            <FilterPanel
              filters={filterConfig.slice(0, 4)}
              columns={isTabletVerticalWidth ? 1 : 4}
            />
          </Grid>

          <Grid container sx={{ width: '100%' }}>
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletVerticalWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: isTabletWidth ? 'column' : 'row',
                  gap: 2,
                  width: '100%',
                  mt: 'auto',
                }}
              >
                {render}
              </Grid>

              <FilterPanel
                filters={filterConfig.slice(4)}
                columns={isTabletVerticalWidth ? 1 : 3}
              />
            </Grid>
          </Grid>

          {isGraphicsOpen && (
            <Grid item xs={12}>
              <OvertimeGraphics />
            </Grid>
          )}
          <Grid item xs={12}>
            {loadingOvertimeReport && (
              <Box
                sx={{ minHeight: '100px' }}
                className="w-100 h-100 d-flex justify-content-center align-items-center"
              >
                <CircularProgress />
              </Box>
            )}

            {!loadingOvertimeReport && (
              <OvertimeTable tableData={tableData} headerItems={headerItems} />
            )}
            {!loadingOvertimeReport && !tableData.length && (
              <Box>
                <Grid
                  container
                  justifyContent={'center'}
                  spacing={3}
                  className="my-2"
                >
                  <Box py={4}> No data available</Box>
                </Grid>
              </Box>
            )}
            {paginationComponent}
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

export default ReportOvertimeScreen;
